import { useState, useCallback, useRef, useEffect } from 'react'
import { CellType, CellTypeIcon, CellTypeLabel, CellTypeColor, DEFAULT_CATEGORIES, generateId } from '../utils/gridHelpers.js'

export default function GridEditor({ grid, zones, onPaintCell, onPaintCells, onUndo, onRedo, onSaveZones, onNavigateToList }) {
  const [activeTool, setActiveTool] = useState(CellType.SHELF)
  const [activeZoneCategory, setActiveZoneCategory] = useState(null)
  const [isPainting, setIsPainting] = useState(false)
  const [paintedCells, setPaintedCells] = useState(new Set())
  const gridRef = useRef(null)

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault()
        if (e.shiftKey) {
          onRedo?.()
        } else {
          onUndo?.()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onUndo, onRedo])

  // Build zone lookup: cell key → zone info
  const zoneLookup = useRef(new Map())
  useEffect(() => {
    const lookup = new Map()
    for (const zone of zones) {
      const cells = typeof zone.cells === 'string' ? JSON.parse(zone.cells) : zone.cells
      const cat = DEFAULT_CATEGORIES.find(c => c.id === zone.category) || { color: zone.color }
      for (const [x, y] of cells) {
        lookup.set(`${x},${y}`, { category: zone.category, color: cat.color || zone.color })
      }
    }
    zoneLookup.current = lookup
  }, [zones])

  const handleCellDown = useCallback((x, y, e) => {
    e.preventDefault()

    // Right-click → erase
    if (e.button === 2) {
      onPaintCell(x, y, CellType.EMPTY)
      return
    }

    // Zone painting mode
    if (activeZoneCategory) {
      toggleZoneCell(x, y, activeZoneCategory)
      return
    }

    setIsPainting(true)
    setPaintedCells(new Set([`${x},${y}`]))
    onPaintCell(x, y, activeTool)
  }, [activeTool, activeZoneCategory, onPaintCell])

  const handleCellEnter = useCallback((x, y) => {
    if (!isPainting || activeZoneCategory) return
    const key = `${x},${y}`
    if (paintedCells.has(key)) return
    setPaintedCells(prev => new Set(prev).add(key))
    onPaintCell(x, y, activeTool)
  }, [isPainting, activeTool, activeZoneCategory, paintedCells, onPaintCell])

  const handleMouseUp = useCallback(() => {
    setIsPainting(false)
    setPaintedCells(new Set())
  }, [])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [handleMouseUp])

  // Prevent context menu on grid
  const handleContextMenu = useCallback((e) => {
    e.preventDefault()
  }, [])

  // Toggle a cell in a zone
  function toggleZoneCell(x, y, category) {
    const currentZones = [...zones]
    let zone = currentZones.find(z => z.category === category)
    const cat = DEFAULT_CATEGORIES.find(c => c.id === category)

    if (!zone) {
      zone = {
        id: generateId(),
        category,
        cells: [],
        color: cat?.color || '#60A5FA',
      }
      currentZones.push(zone)
    }

    const cells = typeof zone.cells === 'string' ? JSON.parse(zone.cells) : [...zone.cells]
    const existingIdx = cells.findIndex(([cx, cy]) => cx === x && cy === y)

    if (existingIdx >= 0) {
      cells.splice(existingIdx, 1)
    } else {
      cells.push([x, y])
    }

    zone.cells = cells
    const updatedZones = currentZones.map(z => z.category === category ? { ...z, cells: JSON.stringify(cells) } : z)
    onSaveZones(updatedZones)
  }

  if (!grid) return null

  const height = grid.length
  const width = grid[0]?.length || 0

  return (
    <div className="editor">
      <div className="editor__canvas-area">
        {/* Toolbar */}
        <div className="editor__toolbar">
          <div className="editor__toolbar-group">
            {Object.values(CellType).map(type => (
              <button
                key={type}
                className={`editor__tool-btn ${activeTool === type && !activeZoneCategory ? 'editor__tool-btn--active' : ''}`}
                onClick={() => { setActiveTool(type); setActiveZoneCategory(null) }}
                title={CellTypeLabel[type]}
              >
                {CellTypeIcon[type]} {CellTypeLabel[type]}
              </button>
            ))}
          </div>

          <div className="editor__toolbar-sep" />

          <div className="editor__toolbar-group">
            <button className="btn btn--ghost btn--icon" onClick={onUndo} title="Ongedaan maken (⌘Z)">↩</button>
            <button className="btn btn--ghost btn--icon" onClick={onRedo} title="Opnieuw (⌘⇧Z)">↪</button>
          </div>

          <div className="editor__toolbar-sep" />

          <button
            className="btn btn--primary"
            onClick={onNavigateToList}
          >
            📝 Boodschappenlijst
          </button>
        </div>

        {/* Grid */}
        <div
          className="editor__grid-wrapper"
          onContextMenu={handleContextMenu}
          ref={gridRef}
        >
          <div
            className="editor__grid"
            style={{
              gridTemplateColumns: `repeat(${width}, 28px)`,
              gridTemplateRows: `repeat(${height}, 28px)`,
            }}
          >
            {grid.map((row, y) =>
              row.map((cell, x) => {
                const zoneInfo = zoneLookup.current.get(`${x},${y}`)
                const cellClass = cell !== CellType.EMPTY ? `editor__cell--${cell}` : ''
                return (
                  <div
                    key={`${x}-${y}`}
                    className={`editor__cell ${cellClass} ${zoneInfo ? 'editor__cell--zone' : ''}`}
                    style={zoneInfo && cell !== CellType.WALL ? {
                      backgroundColor: zoneInfo.color + '33', // 20% opacity
                      borderColor: zoneInfo.color + '44',
                    } : undefined}
                    onMouseDown={e => handleCellDown(x, y, e)}
                    onMouseEnter={() => handleCellEnter(x, y)}
                  >
                    {zoneInfo && (
                      <span
                        className="editor__cell-zone-dot"
                        style={{ backgroundColor: zoneInfo.color }}
                      />
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="editor__sidebar">
        {/* Zone assignment panel */}
        <div className="panel">
          <div className="panel__header">
            <span className="panel__title">🏷️ Zones toewijzen</span>
          </div>
          <div className="panel__body">
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
              Selecteer een categorie en klik op cellen om zones toe te wijzen.
            </p>
            {DEFAULT_CATEGORIES.map(cat => {
              const zone = zones.find(z => z.category === cat.id)
              const cells = zone ? (typeof zone.cells === 'string' ? JSON.parse(zone.cells) : zone.cells) : []
              return (
                <div
                  key={cat.id}
                  className={`zone-item ${activeZoneCategory === cat.id ? 'zone-item--active' : ''}`}
                  onClick={() => {
                    setActiveZoneCategory(activeZoneCategory === cat.id ? null : cat.id)
                    if (activeZoneCategory !== cat.id) setActiveTool(null)
                  }}
                >
                  <span className="zone-item__color" style={{ backgroundColor: cat.color }} />
                  <span className="zone-item__name">{cat.name}</span>
                  <span className="zone-item__count">{cells.length}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Legend panel */}
        <div className="panel">
          <div className="panel__header">
            <span className="panel__title">📋 Legenda</span>
          </div>
          <div className="panel__body">
            {Object.values(CellType).map(type => (
              <div key={type} className="zone-item" style={{ cursor: 'default' }}>
                <span
                  className="zone-item__color"
                  style={{
                    backgroundColor: CellTypeColor[type] || 'var(--color-surface)',
                    border: type === CellType.EMPTY ? '1px dashed var(--color-border-hover)' : 'none',
                  }}
                />
                <span className="zone-item__name">
                  {CellTypeIcon[type]} {CellTypeLabel[type]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
